import { ApplicationConfig, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { TranslateLoader, TranslateService, provideTranslateService } from '@ngx-translate/core';
import { providePrimeNG } from 'primeng/config';

import { API_CONFIG, resolveApiConfig } from './core/config/api.config';
import { DEFAULT_ORGANIZATION_BRANDING } from './core/config/organization-branding.config';
import { apiLoggingInterceptor } from './core/interceptors/api-logging.interceptor';
import { authErrorInterceptor } from './core/interceptors/auth-error.interceptor';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { ThemeService } from './core/services/theme.service';
import { routes } from './app.routes';

function createTranslateLoader(httpClient: HttpClient): TranslateLoader {
  return {
    getTranslation: (language: string) =>
      httpClient.get<object>(`/assets/i18n/${language}.json`) as ReturnType<TranslateLoader['getTranslation']>
  };
}

function initializeTheme(): () => void {
  return () => {
    const themeService = inject(ThemeService);
    themeService.initializeTheme(DEFAULT_ORGANIZATION_BRANDING);
  };
}

function initializeLanguage(): () => void {
  return () => {
    const translateService = inject(TranslateService);
    const fallbackLanguage = 'en';
    const storedLanguage = localStorage.getItem('app.language');
    const defaultLanguage = storedLanguage === 'es' ? 'es' : fallbackLanguage;

    translateService.addLangs(['en', 'es']);
    translateService.setDefaultLang(fallbackLanguage);
    translateService.use(defaultLanguage);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([apiLoggingInterceptor, authInterceptor, authErrorInterceptor, loadingInterceptor])),
    {
      provide: API_CONFIG,
      useValue: resolveApiConfig()
    },
    providePrimeNG(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTheme,
      multi: true
    }
  ]
};
